import AddIcon from '@diligentcorp/atlas-react-icons/dist/esm/lens/Add.js';
import EditIcon from '@diligentcorp/atlas-react-icons/dist/esm/lens/Edit.js';
import FilterIcon from '@diligentcorp/atlas-react-icons/dist/esm/lens/Filter.js';
import UndoIcon from '@diligentcorp/atlas-react-icons/dist/esm/lens/Undo.js';
import {
  PageHeader,
  StatusIndicator,
} from '@diligentcorp/atlas-theme-mui-lens';
import {
  Box,
  Button,
  Chip,
  IconButton,
  InputAdornment,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import {
  DragDropContext,
  Draggable,
  Droppable,
  DropResult,
  DroppableProvided,
  DraggableProvided,
} from '@hello-pangea/dnd';
import React, { useState, useEffect } from 'react';
import { SystemPermissionRequired } from 'src/components/atoms/SystemPermissionRequired';
import { EmptyState } from 'src/components/atlas/molecules/EmptyState';
import {
  useGetOrganisationCustomFieldsQuery,
  useGetOrganisationCustomFieldSectionsQuery,
  GetOrganisationCustomFieldsQuery,
  CustomFieldFilterLogic,
} from 'src/graphql/generated';
import { SYSTEM_PERMISSIONS } from 'src/permissionsTypes';
import { atlasSemantic } from 'src/theme/atlas/tokens';
import { useCustomFieldMutations } from './hooks/useCustomFieldMutations';
import { FieldManagementAuditLogDrawer } from './components/FieldManagementAuditLogDrawer';
import { CreateCustomFieldSideSheet } from './CreateCustomFieldSideSheet';
import { SectionsTab } from './SectionsTab';
import { CustomFieldsFilterPopover } from './CustomFieldsFilterPopover';
import { customFieldStatusColorMap } from './customFieldStatusColorMap';
import * as styles from './CustomFields.styles';
import { CustomFieldFilterRow } from './customFields.types';
import { getReorderTarget } from './utils/getReorderTarget';

export type CustomField =
  GetOrganisationCustomFieldsQuery['getOrganisationCustomFields']['customFields'][number];

const COLUMNS = [
  'Status',
  'Label',
  'Identifier',
  'Section',
  'Type',
  'Last updated',
  'Created by',
  'Actions',
];

const PAGE_SIZE = 25;

let filterRowSeq = 0;
const makeFilterId = () => `filter-row-${(filterRowSeq += 1)}`;

const FIELD_TYPE_LABELS: Record<string, string> = {
  TEXT: 'Text input',
  SINGLE_SELECT: 'Single select',
};

// INACTIVE fields are surfaced as "Retired" in the UI (DB enum is ACTIVE/INACTIVE).
const STATUS_LABELS: Record<string, string> = {
  ACTIVE: 'Active',
  INACTIVE: 'Retired',
};

export const formatCreatedBy = (createdBy: string): string => createdBy || '—';

export const CustomFields = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  // Draft state is what the popover edits; applied state is what the query
  // uses. They only sync when the user hits Apply, so typing in a free-text
  // value doesn't fire a request per character.
  const [draftFilterRows, setDraftFilterRows] = useState<
    CustomFieldFilterRow[]
  >([{ id: makeFilterId(), column: '', value: '' }]);
  const [appliedFilterRows, setAppliedFilterRows] = useState<
    CustomFieldFilterRow[]
  >([{ id: makeFilterId(), column: '', value: '' }]);
  const [filterAnchorEl, setFilterAnchorEl] = useState<HTMLElement | null>(
    null,
  );
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(PAGE_SIZE);
  const [createSheetOpen, setCreateSheetOpen] = useState(false);
  const [sectionsCreateSheetOpen, setSectionsCreateSheetOpen] = useState(false);
  const [editSheetOpen, setEditSheetOpen] = useState(false);
  const [auditLogOpen, setAuditLogOpen] = useState(false);
  const [fieldToEdit, setFieldToEdit] = useState<CustomField | undefined>(
    undefined,
  );

  const { reorderCustomFields } = useCustomFieldMutations();

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setPage(1);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  const offset = (page - 1) * rowsPerPage;

  const activeFilters = appliedFilterRows.flatMap((row) =>
    row.column && row.value ? [{ column: row.column, value: row.value }] : [],
  );

  const filterLogic = appliedFilterRows.slice(1).some((r) => r.logic === 'or')
    ? CustomFieldFilterLogic.Or
    : CustomFieldFilterLogic.And;

  const hasActiveFilters = activeFilters.length > 0;

  const { data, isLoading } = useGetOrganisationCustomFieldsQuery(
    {
      input: {
        limit: rowsPerPage,
        offset,
        search: debouncedSearch || undefined,
        filters: hasActiveFilters ? activeFilters : undefined,
        logic: hasActiveFilters ? filterLogic : undefined,
      },
    },
    { enabled: true },
  );

  const customFields = data?.getOrganisationCustomFields?.customFields || [];

  const totalCount = data?.getOrganisationCustomFields?.total ?? 0;

  const { data: sectionsData } = useGetOrganisationCustomFieldSectionsQuery();
  const sections =
    sectionsData?.getOrganisationCustomFieldSections?.sections ?? [];
  const sectionNameById = new Map(
    sections.map((section) => [section.id, section.name]),
  );
  const sectionOptions = sections.map((section) => ({
    value: section.id,
    label: section.name,
  }));

  const resetFilters = () => {
    const blankRows = [{ id: makeFilterId(), column: '' as const, value: '' }];
    setDraftFilterRows(blankRows);
    setAppliedFilterRows(blankRows);
    setPage(1);
  };

  const handleReset = () => {
    setSearchTerm('');
    resetFilters();
  };

  const handleApplyFilters = () => {
    setAppliedFilterRows(draftFilterRows);
    setPage(1);
    setFilterAnchorEl(null);
  };

  const handleCloseFilterPopover = () => {
    setDraftFilterRows(appliedFilterRows);
    setFilterAnchorEl(null);
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (newRowsPerPage: number) => {
    setRowsPerPage(newRowsPerPage);
    setPage(1);
  };

  const formatDate = (date: Date | string) => {
    try {
      return new Date(date).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return '-';
    }
  };

  const getFieldTypeLabel = (fieldType: string) => {
    return FIELD_TYPE_LABELS[fieldType] || fieldType;
  };

  const getStatusLabel = (status: string) => {
    return STATUS_LABELS[status] || status;
  };

  const handleEdit = (field: CustomField) => {
    setFieldToEdit(field);
    setEditSheetOpen(true);
  };

  const onDragEnd = (result: DropResult) => {
    const { destination, source } = result;

    if (!destination || destination.index === source.index) {
      return;
    }

    reorderCustomFields({
      fieldId: customFields[source.index].id,
      ...getReorderTarget(
        customFields.map((field) => field.id),
        source.index,
        destination.index,
      ),
    });
  };

  return (
    <Box sx={styles.pageContainer} data-testid="custom-fields-page">
      <PageHeader
        pageTitle="Field management"
        pageSubtitle="Manage custom field settings"
        moreButton={
          <Box sx={styles.headerActions}>
            <SystemPermissionRequired
              permission={SYSTEM_PERMISSIONS.MANAGE_CUSTOM_FIELDS}
            >
              <Button
                variant="text"
                color="primary"
                data-testid="audit-log-button"
                onClick={() => setAuditLogOpen(true)}
              >
                Audit log
              </Button>
            </SystemPermissionRequired>
            {activeTab === 0 && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                data-testid="custom-fields-add-new"
                onClick={() => setCreateSheetOpen(true)}
              >
                Add new field
              </Button>
            )}
          </Box>
        }
      />

      <Tabs
        aria-label="Field management tabs"
        value={activeTab}
        onChange={(_, next: number) => setActiveTab(next)}
        sx={styles.tabs}
      >
        <Tab label="Fields" data-testid="custom-fields-tab-fields" />
        <Tab label="Sections" data-testid="custom-fields-tab-sections" />
      </Tabs>

      {activeTab === 0 && (
        <>
          <Box sx={styles.toolbar}>
            <Box sx={styles.searchField}>
              <Typography sx={styles.searchLabel}>Search</Typography>
              <TextField
                size="medium"
                value={searchTerm}
                placeholder="Search by all fields"
                onChange={(e) => setSearchTerm(e.target.value)}
                slotProps={{
                  htmlInput: { 'data-testid': 'custom-fields-search' },
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <span className="uil uil-search" />
                      </InputAdornment>
                    ),
                  },
                }}
              />
            </Box>

            <Button
              variant="text"
              startIcon={<FilterIcon />}
              data-testid="custom-fields-filter"
              onClick={(e: React.MouseEvent<HTMLElement>) =>
                setFilterAnchorEl(e.currentTarget)
              }
            >
              {hasActiveFilters ? `Filter (${activeFilters.length})` : 'Filter'}
            </Button>
            <CustomFieldsFilterPopover
              anchorEl={filterAnchorEl}
              open={Boolean(filterAnchorEl)}
              onClose={handleCloseFilterPopover}
              rows={draftFilterRows}
              onRowsChange={setDraftFilterRows}
              onRemoveAll={resetFilters}
              onApply={handleApplyFilters}
              makeId={makeFilterId}
              sectionOptions={sectionOptions}
            />

            <Button
              variant="text"
              startIcon={<UndoIcon />}
              data-testid="custom-fields-reset"
              onClick={handleReset}
            >
              Reset
            </Button>
          </Box>

          <DragDropContext onDragEnd={onDragEnd}>
            <TableContainer sx={styles.tableContainer}>
              <Table sx={styles.fieldsTable}>
                <TableHead>
                  <TableRow sx={styles.headerRow}>
                    {COLUMNS.map((column, index) => {
                      const isLast = index === COLUMNS.length - 1;
                      return (
                        <TableCell
                          key={column}
                          sx={{
                            ...styles.headerCell,
                            ...(isLast && {
                              position: 'sticky',
                              right: 0,
                              zIndex: 2,
                              textAlign: 'center',
                              backgroundColor:
                                atlasSemantic.color.action.secondary.hoverFill,
                              boxShadow:
                                '-8px 0 16px -4px rgba(0,0,0,0.2), -2px 0 4px 0 rgba(0,0,0,0.15)',
                            }),
                          }}
                        >
                          {column}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                </TableHead>
                <Droppable droppableId="custom-fields-table">
                  {(provided: DroppableProvided) => (
                    <TableBody
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                    >
                      {!isLoading &&
                        customFields.map((field, index) => (
                          <Draggable
                            key={field.id}
                            draggableId={field.id}
                            index={index}
                            isDragDisabled={field.isStandard}
                          >
                            {(provided: DraggableProvided, snapshot) => (
                              <TableRow
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                                data-testid={`custom-fields-row-${field.id}`}
                                sx={{
                                  cursor: field.isStandard ? 'default' : 'grab',
                                  ...(snapshot.isDragging && {
                                    display: 'table',
                                    tableLayout: 'fixed',
                                    width: '100%',
                                    backgroundColor:
                                      atlasSemantic.color.surface.default,
                                    boxShadow: 2,
                                  }),
                                }}
                              >
                                <TableCell sx={styles.statusCell}>
                                  <StatusIndicator
                                    label={getStatusLabel(field.status)}
                                    customColor={customFieldStatusColorMap(
                                      field.status,
                                    )}
                                    size="small"
                                  />
                                </TableCell>
                                <TableCell sx={styles.labelCell}>
                                  {field.label}
                                </TableCell>
                                <TableCell sx={styles.identifierCell}>
                                  {field.identifier}
                                </TableCell>
                                <TableCell sx={styles.sectionCell}>
                                  {(field.sectionId &&
                                    sectionNameById.get(field.sectionId)) ||
                                    'Ungrouped'}
                                </TableCell>
                                <TableCell sx={styles.typeCell}>
                                  <Chip
                                    variant="outlined"
                                    label={getFieldTypeLabel(field.fieldType)}
                                    sx={styles.typeChip}
                                  />
                                </TableCell>
                                <TableCell sx={styles.dateCell}>
                                  {formatDate(field.updatedAt)}
                                </TableCell>
                                <TableCell sx={styles.createdByCell}>
                                  {formatCreatedBy(field?.author?.name ?? '')}
                                </TableCell>
                                <TableCell
                                  sx={{
                                    ...styles.actionsCell,
                                    textAlign: 'center',
                                    backgroundColor:
                                      atlasSemantic.color.surface.default,
                                    ...(!snapshot.isDragging && {
                                      position: 'sticky',
                                      right: 0,
                                      zIndex: 1,
                                      boxShadow:
                                        '-8px 0 16px -4px rgba(0,0,0,0.2), -2px 0 4px 0 rgba(0,0,0,0.15)',
                                    }),
                                  }}
                                >
                                  <IconButton
                                    aria-label="Edit field"
                                    data-testid={`custom-fields-edit-${field.id}`}
                                    onClick={() => handleEdit(field)}
                                    disabled={field.isStandard}
                                  >
                                    <EditIcon />
                                  </IconButton>
                                </TableCell>
                              </TableRow>
                            )}
                          </Draggable>
                        ))}
                      {provided.placeholder}
                    </TableBody>
                  )}
                </Droppable>
              </Table>
              {!isLoading && customFields.length === 0 && (
                <Box
                  sx={styles.emptyWrapper}
                  data-testid="custom-fields-empty-state"
                >
                  <EmptyState
                    title="No custom fields found"
                    description={
                      debouncedSearch || hasActiveFilters
                        ? 'Try adjusting your search or filters.'
                        : 'Custom fields for your organisation will appear here.'
                    }
                  />
                </Box>
              )}
              {!isLoading && customFields.length > 0 && (
                <Box sx={styles.paginationContainer}>
                  <TablePagination
                    component="div"
                    count={totalCount}
                    page={page - 1}
                    rowsPerPage={rowsPerPage}
                    rowsPerPageOptions={[25, 50, 100]}
                    onPageChange={(_event, newPage) =>
                      handlePageChange(newPage + 1)
                    }
                    onRowsPerPageChange={(event) =>
                      handleRowsPerPageChange(parseInt(event.target.value, 10))
                    }
                    labelRowsPerPage="Rows per page"
                    data-testid="custom-fields-pagination"
                  />
                </Box>
              )}
            </TableContainer>
          </DragDropContext>
        </>
      )}

      {activeTab === 1 && (
        <SectionsTab
          createSheetOpen={sectionsCreateSheetOpen}
          onCloseCreateSheet={() => setSectionsCreateSheetOpen(false)}
        />
      )}

      <CreateCustomFieldSideSheet
        isOpen={createSheetOpen}
        onClose={() => setCreateSheetOpen(false)}
        onSuccess={() => setCreateSheetOpen(false)}
        mode="create"
      />

      <CreateCustomFieldSideSheet
        isOpen={editSheetOpen}
        onClose={() => {
          setEditSheetOpen(false);
          setFieldToEdit(undefined);
        }}
        onSuccess={() => {
          setEditSheetOpen(false);
          setFieldToEdit(undefined);
        }}
        mode="edit"
        fieldToEdit={fieldToEdit}
      />

      <FieldManagementAuditLogDrawer
        open={auditLogOpen}
        onClose={() => setAuditLogOpen(false)}
      />
    </Box>
  );
};
